import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { DirectionsCar as CarIcon } from '@mui/icons-material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

export default function CarSelectList(props) {
  const classes = useStyles();
  const { results, selected, onSelect } = props;
  const [imageErrors, setImageErrors] = React.useState(new Set());

  if (!results || results.length === 0) {
    return null;
  }

  const handleImageError = (itemId) => {
    setImageErrors(prev => new Set(prev).add(itemId));
  };

  const renderIcon = (item) => {
    // Check if item has a thumbnail and hasn't errored
    if ((item.thumbnail || item.image || item.photo) && !imageErrors.has(item.objectID)) {
      const imageUrl = item.thumbnail || item.image || item.photo;
      return (
        <img 
          src={imageUrl} 
          alt={item.label || "Car thumbnail"} 
          className={classes.thumbnail}
          onError={() => handleImageError(item.objectID)}
        />
      );
    }
    
    // Fallback to car icon
    return <CarIcon className={classes.customerIcon} />;
  };

  return (
    <div className={classes.container}>
      <List className={classes.list}>
        {results.map((item, index) => (
          <ListItem
            key={index}
            button
            className={classes.listItem}
            style={{
              backgroundColor: selected === index ? "#f0f8ff" : "white",
              borderLeft: selected === index ? "4px solid #1976d2" : "none"
            }}
            onClick={() => onSelect && onSelect(item, index)}
          >
            <ListItemIcon className={classes.icon}>
              {renderIcon(item)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography 
                  variant="body2" 
                  className={classes.primaryText}
                  dangerouslySetInnerHTML={{
                    __html: item.label
                  }}
                />
              }
            />
          </ListItem>
        ))}
      </List>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 9999,
    backgroundColor: 'white',
    borderRadius: '0 0 8px 8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid #e0e0e0',
    borderTop: 'none',
    maxHeight: '300px',
    overflow: 'auto',
    // Mobile-specific improvements
    [theme.breakpoints.down('sm')]: {
      maxHeight: '50vh', // Use viewport height on mobile
      maxWidth: '100vw', // Ensure it doesn't exceed viewport width
      left: '-16px', // Account for dialog padding
      right: '-16px',
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      borderTop: '1px solid #e0e0e0',
      // Improve scrolling on mobile
      WebkitOverflowScrolling: 'touch',
      // Ensure proper touch scrolling
      overscrollBehavior: 'contain',
    },
  },
  list: {
    padding: 0,
  },
  listItem: {
    padding: '12px 16px',
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
    // Mobile-specific improvements
    [theme.breakpoints.down('sm')]: {
      padding: '16px', // Larger touch targets on mobile
      minHeight: '48px', // Ensure minimum touch target size
      '&:active': {
        backgroundColor: '#e3f2fd', // Visual feedback on touch
      },
    },
  },
  icon: {
    minWidth: '40px',
    position: 'relative',
  },
  customerIcon: {
    color: '#666',
    fontSize: '20px',
  },
  thumbnail: {
    width: '40px',
    height: '40px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
  },
  primaryText: {
    fontSize: '14px',
    lineHeight: '1.4',
  },
  secondaryText: {
    color: '#666',
    fontSize: '12px',
    lineHeight: '1.3',
  },
}));
